import {
  BankAuthorisationResource,
  CreditorBankAccountResource,
  CreditorResource,
  RequiredActionsName,
  RequiredActionsStatus,
  SchemeIdentifier,
} from "@gocardless/api/dashboard/types";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { useMemo, useState } from "react";
import { useRequiredActionList } from "@gocardless/api/dashboard/required-action";

import { additionalCurrencies } from "../constants";

import { Currency } from "src/common/currencies";

export interface UseBankDetailsConfigProps {
  schemeIdentifiers: SchemeIdentifier[];
  primaryAccount?: CreditorBankAccountResource;
  bankAuthorisation?: BankAuthorisationResource;
  creditor?: CreditorResource;
  additionalAccounts: Record<string, CreditorBankAccountResource>;
  copVariantEnabled?: boolean;
}

export const useBankDetailsConfig = ({
  schemeIdentifiers,
  primaryAccount,
  bankAuthorisation,
  creditor,
  additionalAccounts,
  copVariantEnabled = false,
}: UseBankDetailsConfigProps) => {
  // Sets additional currencies to match currencies on creditor scheme identifiers
  const schemeIdentifierCurrencies = schemeIdentifiers
    ? schemeIdentifiers?.reduce(
        (acc, scheme) => ({
          ...acc,
          [scheme.currency as string]:
            additionalCurrencies[scheme.currency as Currency],
        }),
        {}
      )
    : {};

  const hasSchemeIdentifiers = schemeIdentifiers.length > 0;

  const internationalPaymentsEnabled = Boolean(creditor?.fx_payout_currency);

  const [showPrimaryBankForm, setShowPrimaryBankForm] = useState(
    !hasSchemeIdentifiers && !primaryAccount
  );
  const [showAddAdditionalBanks, setShowAddAdditionalBanks] = useState(
    Object.entries(additionalAccounts).length === 0 ? false : true
  );

  const { data: requiredActionsList } = useRequiredActionList();

  const defaultPageTitle =
    bankAuthorisation && !bankAuthorisation.failure_reason
      ? i18n._(
          t({
            id: "setup.bank-account-verification.page-title",
            message: "We’ve successfully connected your bank account",
          })
        )
      : i18n._(
          t({
            id: "setup.bank-details.add-bank-account-page-title",
            message:
              "Add the bank account where you'd like to receive payments",
          })
        );

  const pageTitle = useMemo(() => {
    const bankVerifications = requiredActionsList?.required_actions?.reduce(
      (accumulator: RequiredActionsStatus[], action) => {
        const isMatchingAction =
          (action.name === RequiredActionsName.SendVerificationDeposit &&
            action.details?.account_number_ending?.slice(-2) ===
              primaryAccount?.account_number_ending?.slice(-2)) ||
          (action.name === RequiredActionsName.ProvideBankStatement &&
            action.account_number_ending?.slice(-2) ===
              primaryAccount?.account_number_ending?.slice(-2));

        if (isMatchingAction && action.status) {
          accumulator.push(action.status);
        }

        return accumulator;
      },
      []
    );

    const isSuccessful =
      !!bankVerifications?.length &&
      bankVerifications.every(
        (status) => status === RequiredActionsStatus.Successful
      );

    const variantHeader = isSuccessful
      ? i18n._(t`You’ve successfully added your bank account`)
      : i18n._(
          t({
            id: "setup.bank-details.add-bank-account-page-title",
            message:
              "Add the bank account where you'd like to receive payments",
          })
        );

    return copVariantEnabled ? variantHeader : defaultPageTitle;
  }, [
    copVariantEnabled,
    defaultPageTitle,
    primaryAccount,
    requiredActionsList,
  ]);

  return {
    schemeIdentifierCurrencies,
    hasSchemeIdentifiers,
    internationalPaymentsEnabled,
    showPrimaryBankForm,
    setShowPrimaryBankForm,
    showAddAdditionalBanks,
    setShowAddAdditionalBanks,
    pageTitle,
  };
};
